import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { ScaleItem, TextField } from "@/components/QuestionnaireFields";

function AnamneseForm({ def, a, set }) {
  return def.sections.map((s) => (
    <section key={s.title} className="mb-8" data-testid={`q-section-${s.title}`}>
      <h2 className="text-xs font-bold uppercase tracking-overline text-slate-500 mb-3">{s.title}</h2>
      {s.note && <p className="text-xs text-slate-500 mb-3">{s.note}</p>}
      <div className="space-y-4">{s.fields.map((f) => <TextField key={f.id} f={f} value={a[f.id]} onChange={(v) => set(f.id, v)} />)}</div>
    </section>
  ));
}

function SelbstForm({ def, a, set }) {
  return (
    <>
      <div className="text-xs text-slate-600 bg-slate-50 border border-slate-200 px-4 py-3 mb-6" data-testid="q-scale-hint">
        <b>1</b> = {def.scale.low} · <b>6</b> = {def.scale.high}
      </div>
      {def.sections.map((s) => (
        <section key={s.title} className="mb-8">
          <h2 className="text-sm font-bold text-slate-900 mb-2">{s.title}</h2>
          {s.groups.map((g) => (
            <div key={g.id} className="mb-5">
              {s.groups.length > 1 && <h3 className="text-xs font-bold uppercase tracking-overline text-slate-500 mt-3">{g.title}</h3>}
              {g.items.map((it, i) => <ScaleItem key={it.id} item={it} idx={i + 1} value={a[it.id]} onChange={(v) => set(it.id, v)} />)}
              {g.remark && (
                <textarea rows={2} placeholder="Bemerkungen (optional)" value={a[`${g.id}_bem`] || ""} onChange={(e) => set(`${g.id}_bem`, e.target.value)}
                  className="input-tech resize-y mt-2 text-sm" data-testid={`q-remark-${g.id}`} />
              )}
            </div>
          ))}
        </section>
      ))}
      {def.extra_text.map((x) => <TextField key={x.id} f={{ ...x, type: "text" }} value={a[x.id]} onChange={(v) => set(x.id, v)} />)}
    </>
  );
}

const missingOf = (def, a) => (def.kind === "anamnese"
  ? def.sections.flatMap((s) => s.fields).filter((f) => f.required && !String(a[f.id] || "").trim())
  : def.sections.flatMap((s) => s.groups.flatMap((g) => g.items)).filter((i) => !a[i.id]));

export default function QuestionnairePage() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [a, setA] = useState({});
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  useEffect(() => {
    api.get(`/questionnaires/respond/${token}`).then((r) => { setInfo(r.data); setA(r.data.prefill || {}); })
      .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  }, [token]);
  const set = (k, v) => setA((x) => ({ ...x, [k]: v }));
  if (error) return <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6"><div className="max-w-md bg-white border border-slate-200 p-8 text-center text-sm text-slate-600" data-testid="q-error">{error}</div></div>;
  if (!info) return <div className="min-h-screen flex items-center justify-center text-xs text-slate-400">Lädt…</div>;
  const def = info.definition;
  const missing = missingOf(def, a);
  const submit = async () => {
    if (missing.length) { toast.error(`Bitte noch ${missing.length} Pflichtangabe(n) ausfüllen`); return; }
    setSaving(true);
    try { await api.post(`/questionnaires/respond/${token}`, { answers: a }); setDone(true); window.scrollTo(0, 0); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setSaving(false); }
  };
  const thanks = done || info.already_submitted;
  return (
    <div className="min-h-screen bg-slate-50 flex items-start justify-center p-3 sm:p-8">
      <div className="w-full max-w-2xl bg-white border border-slate-200" data-testid="questionnaire-page">
        <div className="px-5 sm:px-8 py-6 border-b border-slate-100">
          <img src={`${process.env.REACT_APP_BACKEND_URL}/api/assets/logo-email.png`} alt="Logo" className="h-9 mb-5" />
          <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500 mb-1">{def.title}</div>
          <div className="font-heading text-2xl font-light tracking-tighter text-slate-900">{thanks ? "Vielen Dank!" : `Hallo${info.first_name ? " " + info.first_name : ""}.`}</div>
          {!thanks && <p className="text-sm text-slate-600 mt-2">{def.intro}</p>}
        </div>
        <div className="px-5 sm:px-8 py-6">
          {thanks ? (
            <div className="p-6 bg-emerald-50 border border-emerald-200 text-center flex flex-col items-center gap-2" data-testid="q-done">
              <CheckCircle2 size={32} strokeWidth={1.5} className="text-emerald-600" />
              <div className="font-medium text-emerald-800">Deine Antworten wurden übermittelt.</div>
            </div>
          ) : (
            <>
              {def.kind === "anamnese" ? <AnamneseForm def={def} a={a} set={set} /> : <SelbstForm def={def} a={a} set={set} />}
              {def.consent && <p className="text-xs text-slate-500 mt-4">{def.consent}</p>}
              <button onClick={submit} disabled={saving} data-testid="q-submit"
                className="w-full mt-6 bg-slate-900 text-white py-4 text-sm font-medium hover:bg-black disabled:opacity-40 transition-colors">
                {saving ? "Sendet…" : missing.length ? `Absenden (noch ${missing.length} offen)` : "Absenden"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
